import { db } from '../db/index';
import { MUTATION_STATUS } from './mutationLog';
import { MUTATION_HANDLERS } from './mutationHandlers';

const BATCH_LIMIT = 50;
const MAX_ATTEMPTS = 5;

// Reason: per §4.8 these types are "silent drop" — max-failed rows are reaped
// automatically rather than surfaced in the Pending Issues inbox.
const SILENT_DROP_TYPES = new Set(['mark_read', 'mark_unread', 'mark_all_read', 'highlight_delete']);

/**
 * Select up to `limit` candidate rows (pending or failed+attempts<MAX),
 * sorted by created_at ascending.
 * @param {number} [limit=BATCH_LIMIT]
 */
export async function selectDrainBatch(limit = BATCH_LIMIT) {
  const all = await db.table('mutation_log')
    .where('status').anyOf([MUTATION_STATUS.PENDING, MUTATION_STATUS.FAILED])
    .toArray();
  return all
    .filter(r =>
      r.status === MUTATION_STATUS.PENDING ||
      (r.status === MUTATION_STATUS.FAILED && (r.attempts ?? 0) < MAX_ATTEMPTS)
    )
    .sort((a, b) => a.created_at - b.created_at)
    .slice(0, limit);
}

/**
 * Transition the given rows to in_flight inside one Dexie transaction.
 * Returns the rows that were successfully transitioned (skips rows whose
 * status changed under us).
 */
export async function markInFlight(rows) {
  const captured = [];
  await db.transaction('rw', db.table('mutation_log'), async () => {
    for (const row of rows) {
      const cur = await db.table('mutation_log').get(row.key);
      if (!cur) continue;
      if (cur.status !== MUTATION_STATUS.PENDING && cur.status !== MUTATION_STATUS.FAILED) continue;
      await db.table('mutation_log').update(row.key, {
        status: MUTATION_STATUS.IN_FLIGHT,
        in_flight_at: Date.now(),
      });
      captured.push({ ...cur, status: MUTATION_STATUS.IN_FLIGHT });
    }
  });
  return captured;
}

/**
 * Apply a batch response array of { key, status, error? } entries.
 * Re-checks each row's current status; only writes if still in_flight.
 */
export async function applyBatchResult(results) {
  for (const r of results) {
    const cur = await db.table('mutation_log').get(r.key);
    if (!cur || cur.status !== MUTATION_STATUS.IN_FLIGHT) continue;
    if (r.status === 'ok' || r.status === 'duplicate') {
      // Reason: highlight_create writes a neg-id row in db.highlights as
      // optimistic UI. Once the server confirms, drop the neg-id row — the
      // next read of the article's highlights picks up the server pos-id row.
      if (cur.type === 'highlight_create' || cur.type === 'digest_highlight_create') {
        await db.table('highlights').where('pending_key').equals(r.key).delete();
      }
      await db.table('mutation_log').delete(r.key);
    } else {
      await db.table('mutation_log').update(r.key, {
        status: MUTATION_STATUS.FAILED,
        attempts: (cur.attempts ?? 0) + 1,
        last_error: r.error || 'unknown',
      });
    }
  }
}

/**
 * Network error revert: flip in_flight → pending for the captured keys.
 * Re-checks each row's status to avoid stomping a concurrent reset.
 */
export async function revertInFlightToPending(keys, errMsg) {
  for (const key of keys) {
    const cur = await db.table('mutation_log').get(key);
    if (cur && cur.status === MUTATION_STATUS.IN_FLIGHT) {
      await db.table('mutation_log').update(key, {
        status: MUTATION_STATUS.PENDING,
        last_error: errMsg ? String(errMsg) : undefined,
      });
    }
  }
}

/** Flip all in_flight → pending. Used by App.jsx boot + SW activate. */
export async function resetStaleInFlight() {
  return db.table('mutation_log')
    .where('status').equals(MUTATION_STATUS.IN_FLIGHT)
    .modify({ status: MUTATION_STATUS.PENDING });
}

/**
 * Reset a failed mutation so it re-enters the drain queue.
 * Resets attempts to 0 so selectDrainBatch picks it up again.
 */
export async function retryMutation(key) {
  const cur = await db.table('mutation_log').get(key);
  if (!cur || cur.status !== MUTATION_STATUS.FAILED) return;
  await db.table('mutation_log').update(key, {
    status: MUTATION_STATUS.PENDING,
    attempts: 0,
    last_error: undefined,
  });
}

/**
 * Roll back the optimistic entity change for the given mutation, then
 * delete its log row — all in one atomic Dexie transaction.
 * If rollback throws, the transaction aborts and the log row is preserved
 * so the user can retry later.
 */
export async function discardMutation(key) {
  const cur = await db.table('mutation_log').get(key);
  if (!cur) return;
  const handler = MUTATION_HANDLERS[cur.type];
  await db.transaction(
    'rw',
    [db.table('mutation_log'), db.table('articles'), db.table('highlights')],
    async () => {
      if (handler?.rollback) {
        await handler.rollback(db, cur.payload, cur.prior_state);
      }
      await db.table('mutation_log').delete(key);
    }
  );
}

/**
 * Delete failed log rows for silent-drop mutation types (mark_read,
 * mark_unread, mark_all_read, highlight_delete) that have reached MAX_ATTEMPTS.
 * Called at the end of each drain so the Pending Issues inbox stays focused
 * on user-actionable failures.
 */
export async function reapSilentDropFailures() {
  const all = await db.table('mutation_log')
    .where('status').equals(MUTATION_STATUS.FAILED)
    .toArray();
  const toDelete = all
    .filter(r => SILENT_DROP_TYPES.has(r.type) && (r.attempts ?? 0) >= MAX_ATTEMPTS)
    .map(r => r.key);
  if (toDelete.length > 0) {
    await db.table('mutation_log').bulkDelete(toDelete);
  }
}

export const __constants = { BATCH_LIMIT, MAX_ATTEMPTS };
